import { supabase } from "@/shared/integrations/supabase/client";
import type { PropertyAttributeRow } from "@/features/properties/types/propertyAttributes";
import {
  buildRequiredSet,
  type TemplateField,
} from "../pages/hooks/useTemplateOptions";
import { mergeSurveyData, type MergeStats } from "./mergeSurveyData";

// Use untyped alias since property_attributes isn't in generated types
const db = supabase as unknown as any;

function parseData(row: PropertyAttributeRow) {
  return typeof row.data === "string" ? JSON.parse(row.data) : row.data;
}

/**
 * Creates a third survey by merging two existing ones: required questions
 * (per the project template) keep the field-app survey's answers, all other
 * questions take the web survey's answers. The merged row is inserted fresh,
 * so it becomes the property's latest survey.
 */
export async function mergeSurveys(params: {
  projectId: string;
  propertyId: string;
  /** Source for required questions (the field-app survey). */
  requiredSource: PropertyAttributeRow;
  /** Source for everything else (the web survey). */
  optionalSource: PropertyAttributeRow;
}): Promise<{ row: PropertyAttributeRow; stats: MergeStats }> {
  const { projectId, propertyId, requiredSource, optionalSource } = params;

  const { data: template, error: templateError } = await db
    .from("attribute_templates")
    .select("schema_definition")
    .eq("organization_id", projectId)
    .order("version", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (templateError) throw templateError;
  if (!template?.schema_definition) {
    throw new Error("No survey template configured for this project");
  }

  const templateFields: TemplateField[] =
    typeof template.schema_definition === "string"
      ? JSON.parse(template.schema_definition)
      : template.schema_definition;
  const requiredSet = buildRequiredSet(templateFields);

  const { fields, stats } = mergeSurveyData(
    parseData(requiredSource),
    parseData(optionalSource),
    requiredSet
  );

  const { data: row, error: insertError } = await db
    .from("property_attributes")
    .insert({ property_id: propertyId, data: fields })
    .select(
      "id, property_id, captured_at, data, normalization_status, processing_events, last_attempt_at, attempt_count, ref_id"
    )
    .single();

  if (insertError) throw insertError;
  return { row: row as PropertyAttributeRow, stats };
}
